import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Metodický panel pro lektora — zobrazený jen v LiveSession.
 *
 * Tři vrstvy obsahu:
 *  - intro / discussion / takeaway ze seedu (pevné doporučení)
 *  - private + public poznámky lektora pro tuto session (uloží se do session.lecturer_notes_custom)
 *  - AI poznámka (generovaná na základě agregovaných odpovědí, uloží se do lecturer_notes_ai)
 */
export default function LecturerNotesPanel({ sessionId, challenge, topicContext, topic }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState({ custom: {}, ai: {}, ai_topic: {} });
  const [pvt, setPvt] = useState("");
  const [pub, setPub] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [generatingAi, setGeneratingAi] = useState(false);
  const [generatingTopicAi, setGeneratingTopicAi] = useState(false);

  const cid = challenge?.id;
  const tid = topic?.id || topicContext?.topic_id;

  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      try {
        const { data } = await api.get(`/sessions/${sessionId}/notes`);
        setNotes({ custom: data.custom || {}, ai: data.ai || {}, ai_topic: data.ai_topic || {} });
      } catch {
        /* no-op */
      }
    })();
  }, [sessionId]);

  useEffect(() => {
    if (!cid) return;
    const c = notes.custom?.[cid] || {};
    setPvt(c.private || "");
    setPub(c.public || "");
  }, [cid, notes.custom]);

  if (!challenge) return null;
  const seed = challenge.lecturer_notes;
  const aiEntry = notes.ai?.[cid];
  const aiTopicEntry = tid ? notes.ai_topic?.[tid] : null;
  const topicSeed = topic?.lecturer_notes;

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await api.post(`/sessions/${sessionId}/notes`, {
        challenge_id: cid,
        private: pvt,
        public: pub,
      });
      setNotes((n) => ({
        ...n,
        custom: { ...n.custom, [cid]: { private: pvt, public: pub } },
      }));
      toast.success("Poznámky uloženy");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nepodařilo se uložit");
    } finally {
      setSavingNote(false);
    }
  };

  const generateAi = async () => {
    setGeneratingAi(true);
    try {
      const { data } = await api.post(`/sessions/${sessionId}/notes/${cid}/ai`);
      setNotes((n) => ({
        ...n,
        ai: { ...n.ai, [cid]: { text: data.text, generated_at: new Date().toISOString() } },
      }));
      toast.success("AI poznámka vygenerována");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI generace selhala");
    } finally {
      setGeneratingAi(false);
    }
  };

  const generateTopicAi = async () => {
    if (!tid) return;
    setGeneratingTopicAi(true);
    try {
      const { data } = await api.post(`/sessions/${sessionId}/notes-topic/${tid}/ai`);
      setNotes((n) => ({
        ...n,
        ai_topic: { ...n.ai_topic, [tid]: { text: data.text, generated_at: new Date().toISOString() } },
      }));
      toast.success("AI reflexe tématu vygenerována");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI generace selhala");
    } finally {
      setGeneratingTopicAi(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="lecturer-notes-panel">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-slate-50"
        data-testid="btn-toggle-notes"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">📚</span>
          <span className="font-heading font-black text-slate-900">Metodika pro lektora</span>
          <span className="text-xs text-slate-500">— ke „{challenge.title}"</span>
        </div>
        <span className="text-slate-400 text-sm">{open ? "▲ skrýt" : "▼ rozbalit"}</span>
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-5 border-t border-slate-100 pt-4">
          {/* Topic-level methodology + AI (over WHOLE topic) */}
          {topic && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 space-y-3" data-testid="topic-methodology">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-black uppercase tracking-widest text-blue-800">🎯 Téma</span>
                <span className="text-sm font-bold text-slate-900">{topic.title}</span>
                {topicContext && (
                  <span className="text-[10px] text-slate-500 ml-auto tabular-nums">
                    {topicContext.topic_index}/{topicContext.total_topics}
                  </span>
                )}
              </div>
              {topic.intro && (
                <div className="text-sm text-slate-800">{topic.intro}</div>
              )}
              {topicSeed && (topicSeed.intro || (topicSeed.discussion || []).length || topicSeed.takeaway) && (
                <div className="text-sm text-slate-800 space-y-2 pt-1">
                  {topicSeed.intro && <div><span className="font-bold">Úvod tématu · </span>{topicSeed.intro}</div>}
                  {Array.isArray(topicSeed.discussion) && topicSeed.discussion.length > 0 && (
                    <ul className="list-disc pl-5 space-y-0.5">
                      {topicSeed.discussion.map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                  )}
                  {topicSeed.takeaway && <div className="font-medium">→ {topicSeed.takeaway}</div>}
                </div>
              )}
              <div className="border-t border-blue-200 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] font-black uppercase tracking-widest text-blue-800">
                    🤖 AI shrnutí celého tématu
                  </div>
                  <button
                    onClick={generateTopicAi}
                    disabled={generatingTopicAi || !tid}
                    className="text-xs font-bold text-blue-700 hover:text-blue-900 disabled:opacity-60"
                    data-testid="btn-generate-ai-topic"
                  >
                    {generatingTopicAi ? "Generuji…" : aiTopicEntry ? "↻ Regenerovat" : "✨ Vygenerovat"}
                  </button>
                </div>
                {aiTopicEntry?.text ? (
                  <div className="bg-white border border-blue-200 rounded p-3 text-sm text-slate-800 whitespace-pre-wrap" data-testid="ai-topic-text">
                    {aiTopicEntry.text}
                  </div>
                ) : (
                  <div className="text-xs text-blue-700/70 italic">
                    Až žáci proberou téma, klikni „Vygenerovat" — AI shrne vzorce napříč všemi otázkami tématu.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Seed notes — pevné metodické tipy */}
          {seed ? (
            <div className="space-y-3">
              {seed.intro && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Úvod</div>
                  <div className="text-sm text-slate-800">{seed.intro}</div>
                </div>
              )}
              {Array.isArray(seed.discussion) && seed.discussion.length > 0 && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Otázky k debatě</div>
                  <ul className="list-disc pl-5 text-sm text-slate-800 space-y-1">
                    {seed.discussion.map((d, i) => (
                      <li key={i}>{d}</li>
                    ))}
                  </ul>
                </div>
              )}
              {seed.takeaway && (
                <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r">
                  <div className="text-[10px] font-black uppercase tracking-widest text-amber-800 mb-1">Klíčové sdělení</div>
                  <div className="text-sm text-slate-900 font-medium">{seed.takeaway}</div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-slate-500 italic">Pro tuto výzvu nejsou k dispozici metodické tipy.</div>
          )}

          {/* AI generated note */}
          <div className="border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                🤖 AI reflexe výsledků třídy
              </div>
              <button
                onClick={generateAi}
                disabled={generatingAi}
                className="text-xs font-bold text-blue-600 hover:text-blue-800 disabled:opacity-60"
                data-testid="btn-generate-ai-note"
              >
                {generatingAi ? "Generuji…" : aiEntry ? "↻ Regenerovat" : "✨ Vygenerovat"}
              </button>
            </div>
            {aiEntry?.text ? (
              <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm text-slate-800 whitespace-pre-wrap">
                {aiEntry.text}
              </div>
            ) : (
              <div className="text-xs text-slate-400 italic">
                Až žáci odpoví, klikni „Vygenerovat" — AI připraví poznámky reflektující výsledky a styl dnešní generace.
              </div>
            )}
          </div>

          {/* Custom lecturer notes */}
          <div className="border-t border-slate-100 pt-4 space-y-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                🔒 Tvoje soukromé poznámky (vidíš jen ty)
              </label>
              <textarea
                value={pvt}
                onChange={(e) => setPvt(e.target.value)}
                rows={2}
                placeholder="Co si potřebuješ pamatovat / na co reagovat ve třídě"
                className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                data-testid="input-private-note"
              />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                👀 Veřejná poznámka (objeví se v reportu pro školu)
              </label>
              <textarea
                value={pub}
                onChange={(e) => setPub(e.target.value)}
                rows={2}
                placeholder="Krátký komentář k této výzvě, který chceš zachytit pro PDF / e-mail report"
                className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                data-testid="input-public-note"
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={saveNote}
                disabled={savingNote}
                className="bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-bold rounded-md px-3 py-2 text-xs"
                data-testid="btn-save-notes"
              >
                {savingNote ? "Ukládám…" : "Uložit poznámky"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
